import { Navbar } from '@denqfabrik/landing-ui';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { dePathToEn, enPathToDe, useTranslation } from '../../i18n';

interface BlogPostPreview {
  title: string;
  slug: string;
}

function stripLangPrefix(pathname: string) {
  const stripped = pathname.replace(/^\/(de|en)(?=\/|$)/, '');
  return stripped.length > 0 ? stripped : '/';
}

export default function AppNavbar({ onOpenFunnel }: { onOpenFunnel?: () => void } = {}) {
  const { lang, t } = useTranslation();
  const location = useLocation();
  const [latestPosts, setLatestPosts] = useState<BlogPostPreview[]>([]);

  const withLang = (path: string) => {
    const prefix = lang === 'en' ? '/en' : '/de';
    return path === '/' ? prefix : `${prefix}${path}`;
  };

  // Fetch latest blog posts for dropdown
  useEffect(() => {
    fetch(`/api/blog?lang=${lang}&limit=3`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.posts) {
          setLatestPosts(
            data.posts.slice(0, 3).map((p: { title: string; slug: string }) => ({
              title: p.title,
              slug: p.slug,
            })),
          );
        }
      })
      .catch(() => {});
  }, [lang]);

  const pathWithoutLang = stripLangPrefix(location.pathname);
  const deSlug = lang === 'en' ? enPathToDe(pathWithoutLang) : pathWithoutLang;
  const enSlug = lang === 'de' ? dePathToEn(pathWithoutLang) : pathWithoutLang;
  const dePath = deSlug === '/' ? '/de' : `/de${deSlug}`;
  const enPath = enSlug === '/' ? '/en' : `/en${enSlug}`;

  return (
    <Navbar
      logo={{
        light: '/images/icons/logo-nobackground.svg',
        dark: '/images/icons/logo-nobackground-footer.svg',
        alt: 'denqfabrik',
      }}
      brandName="Denqfabrik"
      homeLink={withLang('/')}
      links={[
        { label: t.nav.safeAgents, to: withLang('/ki-agenten') },
        { label: t.nav.integrations, to: withLang('/integrationen') },
        { label: t.nav.pricing, to: withLang('/pricing') },
      ]}
      dropdown={{
        label: t.nav.resources,
        items: [
          {
            to: withLang('/use-cases'),
            label: t.nav.useCases,
            description: t.nav.resourcesProjectsDesc,
            icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
            iconColorLight: 'bg-emerald-50 text-emerald-600',
            iconColorDark: 'bg-emerald-500/15 text-emerald-400',
          },
          {
            to: withLang('/sicherheit'),
            label: t.nav.safetyLabel,
            description: t.nav.resourcesSafetyDesc,
            icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
            iconColorLight: 'bg-amber-50 text-amber-600',
            iconColorDark: 'bg-amber-500/15 text-amber-400',
          },
          {
            to: withLang('/blog'),
            label: t.nav.blog,
            description: t.nav.resourcesBlogDesc,
            icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z',
            iconColorLight: 'bg-violet-50 text-violet-600',
            iconColorDark: 'bg-violet-500/15 text-violet-400',
          },
          {
            to: withLang('/ueber-uns'),
            label: t.nav.about,
            description: t.nav.resourcesAboutDesc,
            icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
            iconColorLight: 'bg-slate-100 text-slate-600',
            iconColorDark: 'bg-slate-500/15 text-slate-400',
          },
        ],
      }}
      blogPosts={latestPosts.map((p) => ({
        title: p.title,
        slug: p.slug,
        linkTo: withLang(`/blog/${p.slug}`),
      }))}
      blogPostsLabel={t.nav.resourcesLatestPosts}
      languageSwitch={{
        current: lang,
        dePath,
        enPath,
      }}
      cta={{
        // Opens the configurator overlay. `to` stays a real route so the link keeps
        // working without JS and remains crawlable — onClick preventDefaults.
        label: onOpenFunnel ? t.nav.configurator : t.nav.cta,
        to: withLang('/kontakt'),
        onClick: onOpenFunnel,
      }}
    />
  );
}
